import React, { Component, ErrorInfo, ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { FileQuestion, RefreshCw, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface Props {
  children: ReactNode;
  onReset?: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Course-specific Error Boundary
 * 
 * Handles errors in PDF processing, quiz generation, and course content.
 * Provides contextual error messages and recovery options.
 */
export class CourseErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log to Sentry with course-specific context
    Sentry.withScope((scope) => {
      scope.setTag('errorBoundary', 'course');
      scope.setContext('courseError', {
        componentStack: errorInfo.componentStack,
        errorMessage: error.message,
      });
      Sentry.captureException(error);
    });

    if (import.meta.env.DEV) {
      console.error('CourseErrorBoundary caught error:', error);
      console.error('Component stack:', errorInfo.componentStack);
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
    });
    this.props.onReset?.();
  };

  handleGoBack = () => {
    window.history.back();
  };

  render() {
    if (this.state.hasError) {
      const isProcessingError = this.state.error?.message.includes('process') || 
                                this.state.error?.message.includes('PDF') ||
                                this.state.error?.message.includes('generate');

      return (
        <div className="min-h-[400px] flex items-center justify-center p-4">
          <div className="max-w-lg w-full bg-card border border-border rounded-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-orange-500/10 rounded-lg">
                <FileQuestion className="w-6 h-6 text-orange-500" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">
                  {isProcessingError ? 'Processing Error' : 'Content Error'}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {isProcessingError 
                    ? 'Failed to process your course material'
                    : 'Unable to load course content'}
                </p>
              </div>
            </div>

            {import.meta.env.DEV && this.state.error && (
              <div className="mb-4 p-3 bg-muted rounded-md">
                <p className="text-xs font-mono text-destructive">
                  {this.state.error.toString()}
                </p>
              </div>
            )}

            <div className="mb-6 space-y-2">
              <p className="text-sm text-muted-foreground">
                This could be due to:
              </p>
              <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1">
                <li>Invalid or corrupted PDF file</li>
                <li>Network connectivity issues</li>
                <li>Temporary server problems</li>
                {isProcessingError && <li>AI service unavailable</li>}
              </ul>
            </div>

            <p className="text-sm text-muted-foreground mb-6">
              Try uploading your file again or contact support if the problem persists.
            </p>

            <div className="flex gap-3">
              <Button
                onClick={this.handleReset}
                variant="default"
                className="flex-1"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Try Again
              </Button>
              <Button
                onClick={this.handleGoBack}
                variant="outline"
                className="flex-1"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Go Back
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
